<template>
  <div v-if="visible" class="hist-modal-backdrop">
    <div class="hist-modal-card">
      <header class="hist-modal-header">
        <div>
          <p class="hist-modal-title">
            Historial de orden
            <span v-if="ordenResumen" class="hist-modal-title__folio">
              · Folio {{ ordenResumen.folio }} · Versión actual {{ ordenResumen.version }}
            </span>
          </p>
          <p class="hist-modal-subtitle">
            Revisa versiones anteriores y, si lo necesitas, compáralas contra otra versión.
          </p>
        </div>
        <button type="button" class="hist-modal-close" @click="$emit('close')">
          ✕
        </button>
      </header>

      <div class="hist-modal-body" v-if="!cargandoHistorial && !errorCarga">
        <div class="hist-layout">
          <!-- Lista de versiones -->
          <aside class="hist-sidebar">
            <p class="hist-sidebar-title">Versiones</p>

            <button
              type="button"
              class="hist-version-pill hist-version-pill--actual"
              :class="{ 'is-active': selectedKey === 'actual' }"
              @click="seleccionarVersionSidebar('actual')"
            >
              <span class="hist-version-pill__version">Actual</span>
              <span class="hist-version-pill__meta">
                v{{ ordenResumen?.version }} · {{ ordenResumen?.status }}
              </span>
            </button>

            <div class="hist-version-list">
              <button
                v-for="item in historial"
                :key="item.id_historial"
                type="button"
                class="hist-version-pill"
                :class="{ 'is-active': selectedKey === item.id_historial }"
                @click="seleccionarVersionSidebar(item.id_historial)"
              >
                <span class="hist-version-pill__version">
                  v{{ item.version }}
                </span>
                <span class="hist-version-pill__meta">
                  {{ etiquetaTipo(item) }}
                </span>
                <span class="hist-version-pill__date">
                  {{ formatFecha(item.fecha_registro) }}
                </span>
              </button>
            </div>
          </aside>

          <!-- Contenido / vista & comparador -->
          <section class="hist-content">
            <!-- Toolbar modo -->
            <div class="hist-toolbar">
              <div class="hist-toolbar-info">
                <p class="hist-toolbar-title">
                  {{ modoComparar ? 'Comparar versiones' : 'Ver una sola versión' }}
                </p>
                <p class="hist-toolbar-sub">
                  <span v-if="!modoComparar">
                    Selecciona una versión en la columna izquierda para verla completa.
                  </span>
                  <span v-else>
                    El resumen de cambios se interpreta siempre de la versión más antigua a la más reciente,
                    sin importar en qué lado estén.
                  </span>
                </p>
              </div>
              <div class="hist-toolbar-actions">
                <button
                  type="button"
                  class="hist-mode-btn"
                  :class="{ 'is-active': !modoComparar }"
                  @click="activarModoVerSolo"
                >
                  Ver versión
                </button>
                <button
                  type="button"
                  class="hist-mode-btn"
                  :class="{ 'is-active': modoComparar }"
                  @click="activarModoComparar"
                  :disabled="!puedeComparar"
                >
                  Comparar versiones
                </button>
              </div>
            </div>

            <!-- Loading local de versiones -->
            <div
              v-if="
                (!modoComparar && cargandoVersionSeleccionada) ||
                (modoComparar && (cargandoVersionBase || cargandoVersionComparada))
              "
              class="hist-loading"
            >
              Cargando versiones…
            </div>

            <template v-else>
              <!-- MODO VER SOLO UNA VERSIÓN -->
              <div v-if="!modoComparar" class="hist-single-panel">
                <div v-if="versionSeleccionada" class="hist-panel hist-panel--single">
                  <div class="hist-panel-header">
                    <div>
                      <p class="hist-panel-title">
                        Versión v{{ versionSeleccionada.encabezado.version }}
                      </p>
                      <p class="hist-panel-caption">
                        {{ versionSeleccionada.encabezado.status }} ·
                        {{ versionSeleccionada.encabezado.centro_nombre }}
                      </p>
                    </div>
                  </div>

                  <div class="hist-panel-body">
                    <div class="hist-panel-section">
                      <p class="hist-section-title">Encabezado</p>
                      <ul class="hist-kv-list">
                        <li>
                          <span>Versión</span>
                          <strong>v{{ versionSeleccionada.encabezado.version }}</strong>
                        </li>
                        <li>
                          <span>Estatus</span>
                          <strong>{{ versionSeleccionada.encabezado.status }}</strong>
                        </li>
                        <li>
                          <span>Centro</span>
                          <strong>{{ versionSeleccionada.encabezado.centro_nombre }}</strong>
                        </li>
                        <li>
                          <span>Fecha programada</span>
                          <strong>{{ versionSeleccionada.encabezado.fecha_programada }}</strong>
                        </li>
                        <li>
                          <span>Contacto</span>
                          <strong>{{ versionSeleccionada.encabezado.contacto_nombre }}</strong>
                        </li>
                        <li>
                          <span>Teléfono</span>
                          <strong>{{ versionSeleccionada.encabezado.contacto_telefono }}</strong>
                        </li>
                        <li>
                          <span>Correo</span>
                          <strong>{{ versionSeleccionada.encabezado.contacto_email }}</strong>
                        </li>
                      </ul>

                      <div
                        v-if="versionSeleccionada.encabezado.observaciones"
                        class="hist-observaciones"
                      >
                        <p class="hist-section-title">Observaciones</p>
                        <p class="hist-observaciones-text">
                          {{ versionSeleccionada.encabezado.observaciones }}
                        </p>
                      </div>
                    </div>

                    <div class="hist-panel-section">
                      <p class="hist-section-title">
                        Unidades ({{ versionSeleccionada.detalles.length }})
                      </p>
                      <ul class="hist-units-list">
                        <li
                          v-for="detalle in versionSeleccionada.detalles"
                          :key="'sel-det-' + detalle.id_unidad + '-' + (detalle.id_detalle || detalle.id_detalle_historial || '')"
                          class="hist-unit-item"
                        >
                          <div class="hist-unit-main">
                            <span class="hist-unit-name">
                              {{ detalle.producto_nombre || 'Producto' }}
                            </span>
                            <span class="hist-unit-chip">
                              Serie {{ detalle.numero_serie }}
                            </span>
                          </div>
                          <div class="hist-unit-meta">
                            <span
                              class="hist-unit-status"
                              :class="{ 'is-true': esMarcado(detalle.entregado) }"
                            >
                              Entregado: {{ esMarcado(detalle.entregado) ? 'Sí' : 'No' }}
                            </span>
                            <span
                              class="hist-unit-status"
                              :class="{ 'is-true': esMarcado(detalle.devuelto) }"
                            >
                              Devuelto: {{ esMarcado(detalle.devuelto) ? 'Sí' : 'No' }}
                            </span>
                          </div>
                          <p v-if="detalle.observaciones" class="hist-unit-notes">
                            {{ detalle.observaciones }}
                          </p>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <!-- MODO COMPARAR DOS VERSIONES -->
              <div v-else class="hist-panels">
                <!-- Panel base -->
                <div class="hist-panel">
                  <div class="hist-panel-header">
                    <div>
                      <p class="hist-panel-title">Versión base</p>
                      <p v-if="infoComparacion" class="hist-panel-caption">
                        <span
                          v-if="infoComparacion.esBaseMasNueva"
                          class="hist-badge hist-badge--new"
                        >
                          Más reciente
                        </span>
                        <span
                          v-else
                          class="hist-badge hist-badge--old"
                        >
                          Más antigua
                        </span>
                      </p>
                    </div>
                    <select
                      v-model="baseKey"
                      class="hist-select"
                      @change="cargarBaseDesdeSelect"
                    >
                      <option disabled value="">Selecciona versión</option>
                      <option value="actual">
                        Actual (v{{ ordenResumen?.version }})
                      </option>
                      <option
                        v-for="item in historial"
                        :key="'base-' + item.id_historial"
                        :value="item.id_historial"
                      >
                        v{{ item.version }} · {{ etiquetaTipo(item) }}
                      </option>
                    </select>
                  </div>

                  <div v-if="versionBase" class="hist-panel-body">
                    <div class="hist-panel-section">
                      <p class="hist-section-title">Encabezado</p>
                      <ul class="hist-kv-list">
                        <li>
                          <span>Versión</span>
                          <strong>v{{ versionBase.encabezado.version }}</strong>
                        </li>
                        <li>
                          <span>Estatus</span>
                          <strong>{{ versionBase.encabezado.status }}</strong>
                        </li>
                        <li>
                          <span>Centro</span>
                          <strong>{{ versionBase.encabezado.centro_nombre }}</strong>
                        </li>
                        <li>
                          <span>Fecha programada</span>
                          <strong>{{ versionBase.encabezado.fecha_programada }}</strong>
                        </li>
                        <li>
                          <span>Contacto</span>
                          <strong>{{ versionBase.encabezado.contacto_nombre }}</strong>
                        </li>
                        <li>
                          <span>Teléfono</span>
                          <strong>{{ versionBase.encabezado.contacto_telefono }}</strong>
                        </li>
                        <li>
                          <span>Correo</span>
                          <strong>{{ versionBase.encabezado.contacto_email }}</strong>
                        </li>
                      </ul>

                      <div v-if="versionBase.encabezado.observaciones" class="hist-observaciones">
                        <p class="hist-section-title">Observaciones</p>
                        <p class="hist-observaciones-text">
                          {{ versionBase.encabezado.observaciones }}
                        </p>
                      </div>
                    </div>

                    <div class="hist-panel-section">
                      <p class="hist-section-title">
                        Unidades ({{ versionBase.detalles.length }})
                      </p>
                      <ul class="hist-units-list">
                        <li
                          v-for="detalle in versionBase.detalles"
                          :key="'base-det-' + detalle.id_unidad + '-' + (detalle.id_detalle || detalle.id_detalle_historial || '')"
                          class="hist-unit-item"
                        >
                          <div class="hist-unit-main">
                            <span class="hist-unit-name">
                              {{ detalle.producto_nombre || 'Producto' }}
                            </span>
                            <span class="hist-unit-chip">
                              Serie {{ detalle.numero_serie }}
                            </span>
                          </div>
                          <div class="hist-unit-meta">
                            <span
                              class="hist-unit-status"
                              :class="{ 'is-true': esMarcado(detalle.entregado) }"
                            >
                              Entregado: {{ esMarcado(detalle.entregado) ? 'Sí' : 'No' }}
                            </span>
                            <span
                              class="hist-unit-status"
                              :class="{ 'is-true': esMarcado(detalle.devuelto) }"
                            >
                              Devuelto: {{ esMarcado(detalle.devuelto) ? 'Sí' : 'No' }}
                            </span>
                          </div>
                          <p v-if="detalle.observaciones" class="hist-unit-notes">
                            {{ detalle.observaciones }}
                          </p>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <!-- Panel comparado -->
                <div class="hist-panel">
                  <div class="hist-panel-header">
                    <div>
                      <p class="hist-panel-title">Versión comparada</p>
                      <p v-if="infoComparacion" class="hist-panel-caption">
                        <span
                          v-if="!infoComparacion.esBaseMasNueva"
                          class="hist-badge hist-badge--new"
                        >
                          Más reciente
                        </span>
                        <span
                          v-else
                          class="hist-badge hist-badge--old"
                        >
                          Más antigua
                        </span>
                      </p>
                    </div>
                    <select
                      v-model="comparadaKey"
                      class="hist-select"
                      @change="cargarComparadaDesdeSelect"
                    >
                      <option disabled value="">Selecciona versión</option>
                      <option value="actual">
                        Actual (v{{ ordenResumen?.version }})
                      </option>
                      <option
                        v-for="item in historial"
                        :key="'cmp-' + item.id_historial"
                        :value="item.id_historial"
                      >
                        v{{ item.version }} · {{ etiquetaTipo(item) }}
                      </option>
                    </select>
                  </div>

                  <div v-if="versionComparada" class="hist-panel-body">
                    <div class="hist-panel-section">
                      <p class="hist-section-title">Encabezado</p>
                      <ul class="hist-kv-list">
                        <li>
                          <span>Versión</span>
                          <strong>v{{ versionComparada.encabezado.version }}</strong>
                        </li>
                        <li>
                          <span>Estatus</span>
                          <strong>{{ versionComparada.encabezado.status }}</strong>
                        </li>
                        <li>
                          <span>Centro</span>
                          <strong>{{ versionComparada.encabezado.centro_nombre }}</strong>
                        </li>
                        <li>
                          <span>Fecha programada</span>
                          <strong>{{ versionComparada.encabezado.fecha_programada }}</strong>
                        </li>
                        <li>
                          <span>Contacto</span>
                          <strong>{{ versionComparada.encabezado.contacto_nombre }}</strong>
                        </li>
                        <li>
                          <span>Teléfono</span>
                          <strong>{{ versionComparada.encabezado.contacto_telefono }}</strong>
                        </li>
                        <li>
                          <span>Correo</span>
                          <strong>{{ versionComparada.encabezado.contacto_email }}</strong>
                        </li>
                      </ul>

                      <div
                        v-if="versionComparada.encabezado.observaciones"
                        class="hist-observaciones"
                      >
                        <p class="hist-section-title">Observaciones</p>
                        <p class="hist-observaciones-text">
                          {{ versionComparada.encabezado.observaciones }}
                        </p>
                      </div>
                    </div>

                    <div class="hist-panel-section">
                      <p class="hist-section-title">
                        Unidades ({{ versionComparada.detalles.length }})
                      </p>
                      <ul class="hist-units-list">
                        <li
                          v-for="detalle in versionComparada.detalles"
                          :key="'cmp-det-' + detalle.id_unidad + '-' + (detalle.id_detalle || detalle.id_detalle_historial || '')"
                          class="hist-unit-item"
                        >
                          <div class="hist-unit-main">
                            <span class="hist-unit-name">
                              {{ detalle.producto_nombre || 'Producto' }}
                            </span>
                            <span class="hist-unit-chip">
                              Serie {{ detalle.numero_serie }}
                            </span>
                          </div>
                          <div class="hist-unit-meta">
                            <span
                              class="hist-unit-status"
                              :class="{ 'is-true': esMarcado(detalle.entregado) }"
                            >
                              Entregado: {{ esMarcado(detalle.entregado) ? 'Sí' : 'No' }}
                            </span>
                            <span
                              class="hist-unit-status"
                              :class="{ 'is-true': esMarcado(detalle.devuelto) }"
                            >
                              Devuelto: {{ esMarcado(detalle.devuelto) ? 'Sí' : 'No' }}
                            </span>
                          </div>
                          <p v-if="detalle.observaciones" class="hist-unit-notes">
                            {{ detalle.observaciones }}
                          </p>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Resumen de diferencias -->
              <div
                v-if="modoComparar && versionBase && versionComparada"
                class="hist-diff"
              >
                <p class="hist-section-title">
                  Diferencias detectadas
                  <span v-if="infoComparacion" class="hist-diff-sub">
                    (Cambios de v{{ infoComparacion.desde }} → v{{ infoComparacion.hasta }})
                  </span>
                </p>

                <div class="hist-diff-group">
                  <p class="hist-diff-group-title">Encabezado</p>
                  <ul class="hist-diff-list">
                    <li
                      v-for="campo in diffEncabezado"
                      :key="campo.key"
                      :class="['hist-diff-item', { 'is-changed': campo.cambiado }]"
                    >
                      <span class="hist-diff-field">{{ campo.label }}</span>
                      <span class="hist-diff-value">{{ campo.base || '—' }}</span>
                      <span class="hist-diff-arrow">→</span>
                      <span class="hist-diff-value">{{ campo.comparada || '—' }}</span>
                    </li>
                  </ul>
                </div>

                <div class="hist-diff-group">
                  <p class="hist-diff-group-title">Unidades</p>
                  <ul class="hist-diff-list">
                    <li
                      v-for="item in diffUnidades"
                      :key="item.key"
                      :class="['hist-diff-item', 'hist-diff-item--unit', 'is-' + item.tipo]"
                    >
                      <div class="hist-diff-unit-main">
                        <span class="hist-diff-unit-name">
                          {{ item.producto || 'Producto' }}
                        </span>
                        <span class="hist-diff-unit-serie">
                          Serie {{ item.serie }}
                        </span>
                      </div>
                      <div class="hist-diff-unit-tags">
                        <span
                          v-if="item.tipo === 'nuevo'"
                          class="hist-tag hist-tag--nuevo"
                        >
                          Nueva en versión reciente
                        </span>
                        <span
                          v-if="item.tipo === 'eliminado'"
                          class="hist-tag hist-tag--eliminado"
                        >
                          Eliminada en versión reciente
                        </span>
                        <span
                          v-if="item.tipo === 'modificado'"
                          class="hist-tag hist-tag--modificado"
                        >
                          Modificada
                        </span>
                      </div>
                      <div v-if="item.cambios" class="hist-diff-unit-notes">
                        <div v-if="item.cambios.observaciones" class="hist-diff-unit-row">
                          <span class="hist-diff-field">Observaciones</span>
                          <span class="hist-diff-value">
                            {{ item.cambios.observaciones.base || '—' }}
                          </span>
                          <span class="hist-diff-arrow">→</span>
                          <span class="hist-diff-value">
                            {{ item.cambios.observaciones.comparada || '—' }}
                          </span>
                        </div>
                        <div v-if="item.cambios.entregado" class="hist-diff-unit-row">
                          <span class="hist-diff-field">Entregado</span>
                          <span class="hist-diff-value">
                            {{ item.cambios.entregado.base ? 'Sí' : 'No' }}
                          </span>
                          <span class="hist-diff-arrow">→</span>
                          <span class="hist-diff-value">
                            {{ item.cambios.entregado.comparada ? 'Sí' : 'No' }}
                          </span>
                        </div>
                        <div v-if="item.cambios.devuelto" class="hist-diff-unit-row">
                          <span class="hist-diff-field">Devuelto</span>
                          <span class="hist-diff-value">
                            {{ item.cambios.devuelto.base ? 'Sí' : 'No' }}
                          </span>
                          <span class="hist-diff-arrow">→</span>
                          <span class="hist-diff-value">
                            {{ item.cambios.devuelto.comparada ? 'Sí' : 'No' }}
                          </span>
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </template>
          </section>
        </div>
      </div>

      <div v-else-if="cargandoHistorial" class="hist-modal-loading">
        Cargando historial…
      </div>
      <div v-else class="hist-modal-error">
        {{ errorCarga || 'No se pudo cargar el historial.' }}
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ModalHistorialOrdenEntrega',
  props: {
    idOrden: {
      type: [Number, String],
      required: true,
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      cargandoHistorial: false,
      historial: [],
      ordenResumen: null,
      errorCarga: '',

      // Modo de uso
      modoComparar: false,

      // Vista simple (una sola versión)
      selectedKey: '',
      versionSeleccionada: null,
      cargandoVersionSeleccionada: false,

      // Comparación
      baseKey: '',
      comparadaKey: '',
      versionBase: null,
      versionComparada: null,
      cargandoVersionBase: false,
      cargandoVersionComparada: false,
    };
  },
  watch: {
    visible(nuevo) {
      if (nuevo) {
        this.initModal();
      }
    },
  },
  computed: {
    puedeComparar() {
      // Al menos una versión histórica + la actual
      return this.historial && this.historial.length > 0;
    },

    infoComparacion() {
      if (!this.modoComparar || !this.versionBase || !this.versionComparada) return null;
      const vBase = Number(this.versionBase.encabezado?.version ?? 0);
      const vCmp = Number(this.versionComparada.encabezado?.version ?? 0);
      if (!vBase || !vCmp) return null;

      const desde = Math.min(vBase, vCmp);
      const hasta = Math.max(vBase, vCmp);

      return {
        desde,
        hasta,
        esBaseMasNueva: vBase > vCmp,
      };
    },

    orderedVersionsForDiff() {
      if (!this.modoComparar || !this.versionBase || !this.versionComparada) return null;
      const vBase = Number(this.versionBase.encabezado?.version ?? 0);
      const vCmp = Number(this.versionComparada.encabezado?.version ?? 0);

      if (!vBase || !vCmp) {
        return { from: this.versionBase, to: this.versionComparada, fromIsBase: true };
      }

      if (vBase <= vCmp) {
        return { from: this.versionBase, to: this.versionComparada, fromIsBase: true };
      }

      return { from: this.versionComparada, to: this.versionBase, fromIsBase: false };
    },

    diffEncabezado() {
      if (!this.modoComparar || !this.versionBase || !this.versionComparada) return [];

      const campos = [
        { key: 'version', label: 'Versión' },
        { key: 'status', label: 'Estatus' },
        { key: 'id_centro', label: 'Centro (ID)' },
        { key: 'fecha_programada', label: 'Fecha programada' },
        { key: 'contacto_nombre', label: 'Contacto' },
        { key: 'contacto_telefono', label: 'Teléfono' },
        { key: 'contacto_email', label: 'Correo' },
        { key: 'observaciones', label: 'Observaciones' },
      ];

      const baseEnc = this.versionBase.encabezado || {};
      const cmpEnc = this.versionComparada.encabezado || {};

      const ord = this.orderedVersionsForDiff;
      const fromEnc = ord ? ord.from.encabezado || {} : baseEnc;
      const toEnc = ord ? ord.to.encabezado || {} : cmpEnc;

      return campos.map((c) => {
        const baseVal = baseEnc[c.key];
        const cmpVal = cmpEnc[c.key];
        const fromVal = fromEnc[c.key];
        const toVal = toEnc[c.key];

        const changed =
          (fromVal || '').toString().trim() !== (toVal || '').toString().trim();

        return {
          key: c.key,
          label: c.label,
          base: baseVal,
          comparada: cmpVal,
          cambiado: changed,
        };
      });
    },

    diffUnidades() {
      if (!this.modoComparar || !this.versionBase || !this.versionComparada) return [];

      const mapByUnidad = (detalles) => {
        const out = {};
        (detalles || []).forEach((d) => {
          if (d && d.id_unidad != null) {
            out[String(d.id_unidad)] = d;
          }
        });
        return out;
      };

      const baseMap = mapByUnidad(this.versionBase.detalles);
      const cmpMap = mapByUnidad(this.versionComparada.detalles);

      const ord = this.orderedVersionsForDiff || {
        from: this.versionBase,
        to: this.versionComparada,
      };

      const fromMap = mapByUnidad(ord.from.detalles);
      const toMap = mapByUnidad(ord.to.detalles);

      const ids = new Set([
        ...Object.keys(baseMap),
        ...Object.keys(cmpMap),
      ]);

      const resultado = [];

      ids.forEach((idStr) => {
        const id = Number(idStr);
        const a = baseMap[idStr] || null; // valor en panel base
        const b = cmpMap[idStr] || null;  // valor en panel comparado

        const fromUnit = fromMap[idStr] || null;
        const toUnit = toMap[idStr] || null;

        let tipo = 'igual';
        const cambios = {};

        if (fromUnit && !toUnit) {
          // Existía en la versión antigua, ya no está en la más nueva
          tipo = 'eliminado';
        } else if (!fromUnit && toUnit) {
          // No existía en la antigua, aparece en la nueva
          tipo = 'nuevo';
        } else if (fromUnit && toUnit) {
          // Existe en ambas: revisar campos
          if (
            (fromUnit.observaciones || '').trim() !==
            (toUnit.observaciones || '').trim()
          ) {
            cambios.observaciones = {
              base: a?.observaciones,
              comparada: b?.observaciones,
            };
          }

          const entregadoFrom = Number(fromUnit.entregado) === 1;
          const entregadoTo = Number(toUnit.entregado) === 1;
          if (entregadoFrom !== entregadoTo) {
            cambios.entregado = {
              base: a ? Number(a.entregado) === 1 : entregadoFrom,
              comparada: b ? Number(b.entregado) === 1 : entregadoTo,
            };
          }

          const devueltoFrom = Number(fromUnit.devuelto) === 1;
          const devueltoTo = Number(toUnit.devuelto) === 1;
          if (devueltoFrom !== devueltoTo) {
            cambios.devuelto = {
              base: a ? Number(a.devuelto) === 1 : devueltoFrom,
              comparada: b ? Number(b.devuelto) === 1 : devueltoTo,
            };
          }

          if (Object.keys(cambios).length > 0) {
            tipo = 'modificado';
          }
        }

        if (tipo === 'igual') {
          return;
        }

        const ref = b || a || fromUnit || toUnit;
        resultado.push({
          key: `u-${id}`,
          tipo,
          producto: ref?.producto_nombre,
          serie: ref?.numero_serie,
          cambios: Object.keys(cambios).length ? cambios : null,
        });
      });

      const ordenTipo = { nuevo: 1, modificado: 2, eliminado: 3 };
      resultado.sort((x, y) => ordenTipo[x.tipo] - ordenTipo[y.tipo]);

      return resultado;
    },
  },
  methods: {
    esMarcado(valor) {
      return Number(valor) === 1;
    },

    async initModal() {
      if (!this.idOrden) return;
      this.errorCarga = '';
      this.cargandoHistorial = true;

      // reset
      this.historial = [];
      this.ordenResumen = null;
      this.modoComparar = false;
      this.selectedKey = '';
      this.versionSeleccionada = null;
      this.baseKey = '';
      this.comparadaKey = '';
      this.versionBase = null;
      this.versionComparada = null;

      try {
        const { data: json } = await axios.get(
          `/api/almacen/ordenes-entrega/historial/${this.idOrden}`
        );

        if (json.status !== 'ok') {
          throw new Error(json.message || 'Error al cargar historial');
        }

        this.historial = json.data.historial || [];
        this.ordenResumen = json.data.orden || null;

        // Por defecto, ver la versión actual en modo simple
        this.selectedKey = 'actual';
        await this.cargarVersionSeleccionada();
      } catch (e) {
        this.errorCarga = e.message;
      } finally {
        this.cargandoHistorial = false;
      }
    },

    formatFecha(valor) {
      if (!valor) return '';
      // fecha tipo "2025-11-26 15:40:00" o ISO -> nos quedamos con fecha/hora corta
      return valor.replace('T', ' ').slice(0, 19);
    },

    etiquetaTipo(item) {
      if (!item) return '';
      return item.tipo_cambio === 'ajuste' ? 'Ajuste' : 'Edición manual';
    },

    async seleccionarVersionSidebar(key) {
      this.selectedKey = key;

      if (this.modoComparar) {
        this.baseKey = key;
        await this.cargarVersionBase();
      } else {
        await this.cargarVersionSeleccionada();
      }
    },

    async activarModoVerSolo() {
      if (!this.selectedKey) {
        this.selectedKey = 'actual';
      }
      this.modoComparar = false;
      this.baseKey = '';
      this.comparadaKey = '';
      this.versionBase = null;
      this.versionComparada = null;
      await this.cargarVersionSeleccionada();
    },

    async activarModoComparar() {
      if (!this.puedeComparar) return;
      this.modoComparar = true;

      this.baseKey = this.selectedKey || 'actual';

      // Elegimos una comparada por defecto distinta de la base
      if (!this.comparadaKey || this.comparadaKey === this.baseKey) {
        const actualKey = 'actual';
        if (actualKey !== this.baseKey) {
          this.comparadaKey = actualKey;
        } else if (this.historial.length > 0) {
          const otra = this.historial.find(
            (h) => String(h.id_historial) !== String(this.baseKey)
          );
          this.comparadaKey = otra ? otra.id_historial : this.baseKey;
        } else {
          this.comparadaKey = this.baseKey;
        }
      }

      await Promise.all([this.cargarVersionBase(), this.cargarVersionComparada()]);
    },

    async cargarVersionSeleccionada() {
      if (!this.selectedKey) return;
      this.cargandoVersionSeleccionada = true;
      try {
        this.versionSeleccionada = await this.fetchVersionPayload(this.selectedKey);
      } finally {
        this.cargandoVersionSeleccionada = false;
      }
    },

    async cargarBaseDesdeSelect() {
      await this.cargarVersionBase();
      this.selectedKey = this.baseKey;
    },

    async cargarComparadaDesdeSelect() {
      await this.cargarVersionComparada();
    },

    async cargarVersionBase() {
      if (!this.baseKey) return;
      this.cargandoVersionBase = true;
      try {
        this.versionBase = await this.fetchVersionPayload(this.baseKey);
      } finally {
        this.cargandoVersionBase = false;
      }
    },

    async cargarVersionComparada() {
      if (!this.comparadaKey) return;
      this.cargandoVersionComparada = true;
      try {
        this.versionComparada = await this.fetchVersionPayload(this.comparadaKey);
      } finally {
        this.cargandoVersionComparada = false;
      }
    },

    async fetchVersionPayload(key) {
      let url = '';
      if (key === 'actual') {
        url = `/api/almacen/ordenes-entrega/historial/actual/${this.idOrden}`;
      } else {
        url = `/api/almacen/ordenes-entrega/historial/ver/${key}`;
      }

      const { data: json } = await axios.get(url);

      if (json.status !== 'ok') {
        throw new Error(json.message || 'Error al cargar versión');
      }

      return {
        encabezado: json.data.encabezado,
        detalles: json.data.detalles || [],
      };
    },
  },
};
</script>

<style scoped>
/* BACKDROP + CARD PRINCIPAL */
.hist-modal-backdrop {
  position: fixed;
  inset: 0;
  background: radial-gradient(
      circle at 0 0,
      rgba(15, 23, 42, 0.78),
      rgba(15, 23, 42, 0.96)
    );
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  display: flex;
  align-items: flex-start;      /* 👈 ya no centramos verticalmente */
  justify-content: center;
  padding: 1.5rem 0;           /* margen superior/inferior para respirar */
  overflow-y: auto;            /* 👈 scroll vertical en el overlay */
  z-index: 70;
}


.hist-modal-card {
  width: 100%;
  max-width: 1100px;
  /* ❌ quita o comenta esta línea si la tienes:
     max-height: 90vh; */
  border-radius: 1.6rem;
  padding: 1.2rem 1.2rem 1rem;
  background: radial-gradient(
      circle at 0 0,
      rgba(148, 163, 184, 0.3),
      rgba(15, 23, 42, 0.96)
    );
  border: 1px solid rgba(148, 163, 184, 0.7);
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.9);
  color: #e5e7eb;
  display: flex;
  flex-direction: column;

}


/* HEADER */
.hist-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.1rem 0 0.6rem;
  border-bottom: 1px solid rgba(148, 163, 184, 0.6);
}

.hist-modal-title {
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  color: #e5e7eb;
}

.hist-modal-title__folio {
  font-size: 0.9rem;
  font-weight: 500;
  color: #cbd5f5;
}

.hist-modal-subtitle {
  margin-top: 0.15rem;
  font-size: 0.8rem;
  color: #94a3b8;
}

.hist-modal-close {
  border: 1px solid rgba(148, 163, 184, 0.8);
  outline: none;
  cursor: pointer;
  width: 2rem;
  height: 2rem;
  border-radius: 0.9rem;
  font-size: 1rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.98);
  color: #e5e7eb;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.9);
  transition: transform 0.16s ease, box-shadow 0.16s ease, background 0.16s ease,
    border-color 0.16s ease;
}

.hist-modal-close:hover {
  transform: translateY(-1px);
  background: rgba(30, 64, 175, 0.9);
  border-color: rgba(129, 140, 248, 0.9);
}

.hist-modal-close:active {
  transform: translateY(0);
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.95);
}

/* BODY GENERAL */
.hist-modal-body {
  padding-top: 0.7rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;      /* clave para que los hijos puedan scrollear */
}


.hist-layout {
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 0.9rem;
}


/* SIDEBAR VERSIONES */
.hist-sidebar {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 0.6rem 0.55rem 0.7rem;
  border-radius: 1.1rem;
  background: radial-gradient(
      circle at 0 0,
      rgba(148, 163, 184, 0.12),
      rgba(15, 23, 42, 0.98)
    );
  box-shadow: 0 14px 38px rgba(15, 23, 42, 0.9);
  border: 1px solid rgba(148, 163, 184, 0.6);
}

.hist-sidebar-title {
  font-size: 0.78rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #cbd5f5;
  padding: 0 0.2rem;
}

.hist-version-list {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-top: 0.2rem;
  padding-right: 0.1rem;
  max-height: 54vh;
  overflow-y: auto;
}

/* Buttons versión */
.hist-version-pill {
  width: 100%;
  text-align: left;
  border-radius: 0.9rem;
  border: 1px solid rgba(148, 163, 184, 0.6);
  padding: 0.45rem 0.7rem;
  background: rgba(15, 23, 42, 0.96);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.9);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease,
    border-color 0.15s ease;
}

.hist-version-pill--actual {
  background: radial-gradient(
      circle at 0 0,
      rgba(59, 130, 246, 0.18),
      rgba(15, 23, 42, 0.96)
    );
}

.hist-version-pill:hover {
  transform: translateY(-1px);
  border-color: rgba(96, 165, 250, 0.9);
}

.hist-version-pill.is-active {
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.9), rgba(30, 64, 175, 0.95));
  border-color: rgba(129, 140, 248, 0.95);
}

.hist-version-pill__version {
  font-size: 0.8rem;
  font-weight: 600;
  color: #e5e7eb;
}

.hist-version-pill__meta {
  font-size: 0.72rem;
  color: #cbd5f5;
}

.hist-version-pill__date {
  font-size: 0.68rem;
  color: #94a3b8;
}

/* CONTENIDO PRINCIPAL */
.hist-content {
  border-radius: 1.2rem;
  background: radial-gradient(
      circle at 0 0,
      rgba(148, 163, 184, 0.2),
      rgba(15, 23, 42, 0.96)
    );
  box-shadow:
    0 14px 40px rgba(15, 23, 42, 0.95),
    0 0 0 1px rgba(148, 163, 184, 0.5);
  padding: 0.7rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}


.hist-loading,
.hist-modal-loading {
  height: 100%;
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  color: #cbd5f5;
}

.hist-modal-error {
  padding: 1.2rem;
  text-align: center;
  font-size: 0.9rem;
  color: #fecaca;
}

/* TOOLBAR MODO */
.hist-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  padding: 0.35rem 0.3rem 0.5rem;
  border-bottom: 1px solid rgba(148, 163, 184, 0.5);
}

.hist-toolbar-info {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.hist-toolbar-title {
  font-size: 0.9rem;
  font-weight: 500;
  color: #e5e7eb;
}

.hist-toolbar-sub {
  font-size: 0.78rem;
  color: #94a3b8;
}

.hist-toolbar-actions {
  display: flex;
  gap: 0.4rem;
}

.hist-mode-btn {
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.8);
  background: rgba(15, 23, 42, 0.96);
  color: #e5e7eb;
  font-size: 0.78rem;
  padding: 0.25rem 0.85rem;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease,
    box-shadow 0.15s ease;
}

.hist-mode-btn.is-active {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  border-color: rgba(96, 165, 250, 0.95);
  color: #f9fafb;
  box-shadow: 0 10px 26px rgba(37, 99, 235, 0.55);
}

.hist-mode-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

/* PANELES */
.hist-single-panel {
  margin-top: 0.4rem;
  min-height: 0;
  flex: 1;
}

.hist-panels {
  margin-top: 0.4rem;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 0.6rem;
  min-height: 0;
}

.hist-panel {
  border-radius: 1rem;
  background: rgba(15, 23, 42, 0.98);
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.95);
  border: 1px solid rgba(148, 163, 184, 0.5);
  display: flex;
  flex-direction: column;
  min-height: 0;
}


.hist-panel--single {
  max-height: 100%;
}

.hist-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.4rem;
  padding: 0.55rem 0.6rem 0.45rem;
  border-bottom: 1px solid rgba(148, 163, 184, 0.5);
}

.hist-panel-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #e5e7eb;
}

.hist-panel-caption {
  font-size: 0.74rem;
  color: #94a3b8;
  margin-top: 0.1rem;
}

.hist-badge {
  display: inline-block;
  padding: 0.08rem 0.5rem;
  border-radius: 999px;
  font-size: 0.7rem;
  border: 1px solid transparent;
}

.hist-badge--new {
  border-color: rgba(34, 197, 94, 0.9);
  color: #bbf7d0;
  background: rgba(22, 163, 74, 0.25);
}

.hist-badge--old {
  border-color: rgba(148, 163, 184, 0.9);
  color: #cbd5f5;
  background: rgba(15, 23, 42, 0.9);
}

.hist-select {
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.75);
  background: rgba(15, 23, 42, 0.96);
  color: #e5e7eb;
  padding: 0.3rem 0.9rem;
  font-size: 0.78rem;
  outline: none;
  min-width: 160px;
  max-width: 100%;
  appearance: none;
  cursor: pointer;
  background-image: linear-gradient(45deg, transparent 50%, #cbd5f5 50%),
    linear-gradient(135deg, #cbd5f5 50%, transparent 50%);
  background-position: calc(100% - 14px) 50%, calc(100% - 9px) 50%;
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
}

.hist-select:focus {
  border-color: #60a5fa;
  box-shadow: 0 0 0 1px rgba(96, 165, 250, 0.75);
}

.hist-panel-body {
  padding: 0.55rem 0.6rem 0.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  overflow: hidden;
}

.hist-panel-section {
  border-radius: 0.9rem;
  background: rgba(15, 23, 42, 0.98);
  padding: 0.55rem 0.6rem;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.95);
  border: 1px solid rgba(51, 65, 85, 0.9);
}

.hist-section-title {
  font-size: 0.76rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #cbd5f5;
  margin-bottom: 0.35rem;
}

/* LISTA KEY/VALUE ENCABEZADO */
.hist-kv-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.25rem 0.7rem;
  font-size: 0.78rem;
}

.hist-kv-list li {
  display: flex;
  flex-direction: column;
}

.hist-kv-list span {
  font-size: 0.7rem;
  color: #94a3b8;
}

.hist-kv-list strong {
  font-size: 0.82rem;
  color: #e5e7eb;
}

/* OBSERVACIONES */
.hist-observaciones {
  margin-top: 0.5rem;
}

.hist-observaciones-text {
  font-size: 0.78rem;
  color: #e5e7eb;
  background: rgba(15, 23, 42, 0.98);
  border-radius: 0.75rem;
  padding: 0.45rem 0.55rem;
  border: 1px solid rgba(75, 85, 99, 0.9);
  max-height: 110px;
  overflow-y: auto;
}

/* LISTA UNIDADES */
.hist-units-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 200px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.hist-unit-item {
  border-radius: 0.85rem;
  padding: 0.45rem 0.55rem;
  background: rgba(15, 23, 42, 0.96);
  border: 1px solid rgba(51, 65, 85, 0.9);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.95);
}

.hist-unit-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.4rem;
  margin-bottom: 0.15rem;
}

.hist-unit-name {
  font-size: 0.8rem;
  font-weight: 500;
  color: #e5e7eb;
}

.hist-unit-chip {
  font-size: 0.72rem;
  border-radius: 999px;
  padding: 0.15rem 0.55rem;
  background: rgba(30, 64, 175, 0.9);
  color: #e5e7eb;
  border: 1px solid rgba(129, 140, 248, 0.95);
}

.hist-unit-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-bottom: 0.15rem;
}

.hist-unit-status {
  font-size: 0.7rem;
  border-radius: 999px;
  padding: 0.1rem 0.55rem;
  background: rgba(15, 23, 42, 0.96);
  color: #e5e7eb;
  border: 1px solid rgba(75, 85, 99, 0.9);
}

.hist-unit-status.is-true {
  border-color: rgba(34, 197, 94, 0.9);
  color: #bbf7d0;
  background: rgba(22, 163, 74, 0.25);
}

.hist-unit-notes {
  font-size: 0.74rem;
  color: #cbd5f5;
  margin-top: 0.05rem;
}

/* BLOQUE DIFERENCIAS */
.hist-diff {
  margin-top: 0.55rem;
  border-radius: 0.95rem;
  padding: 0.55rem 0.6rem 0.5rem;
  background: rgba(15, 23, 42, 0.98);
  box-shadow: 0 14px 32px rgba(15, 23, 42, 0.95);
  border: 1px solid rgba(51, 65, 85, 0.9);
}

.hist-diff-sub {
  font-size: 0.7rem;
  color: #94a3b8;
  margin-left: 0.25rem;
}

.hist-diff-group {
  margin-top: 0.4rem;
}

.hist-diff-group:first-of-type {
  margin-top: 0.15rem;
}

.hist-diff-group-title {
  font-size: 0.76rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #cbd5f5;
  margin-bottom: 0.25rem;
}

.hist-diff-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.22rem;
}

/* ITEM DIFERENCIA GENÉRICO */
.hist-diff-item {
  border-radius: 0.65rem;
  padding: 0.25rem 0.45rem;
  display: grid;
  grid-template-columns: minmax(0, 140px) minmax(0, 1fr) auto minmax(0, 1fr);
  gap: 0.25rem 0.4rem;
  align-items: center;
  font-size: 0.74rem;
  background: rgba(15, 23, 42, 0.96);
  color: #e5e7eb;
  border: 1px solid rgba(51, 65, 85, 0.9);
}

.hist-diff-item.is-changed {
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.9), rgba(30, 64, 175, 0.95));
  border-color: rgba(129, 140, 248, 0.95);
}

.hist-diff-field {
  font-size: 0.7rem;
  color: #cbd5f5;
}

.hist-diff-item.is-changed .hist-diff-field {
  color: #e5e7eb;
}

.hist-diff-value {
  font-size: 0.75rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hist-diff-arrow {
  font-size: 0.8rem;
  text-align: center;
  opacity: 0.8;
}

/* ITEMS DIFERENCIA UNIDADES */
.hist-diff-item--unit {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.3rem;
  padding: 0.4rem 0.5rem;
  background: rgba(15, 23, 42, 0.96);
}

.hist-diff-item--unit.is-nuevo {
  border-color: rgba(34, 197, 94, 0.9);
}

.hist-diff-item--unit.is-eliminado {
  border-color: rgba(248, 113, 113, 0.95);
}

.hist-diff-item--unit.is-modificado {
  border-color: rgba(96, 165, 250, 0.95);
}

.hist-diff-unit-main {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  align-items: center;
}

.hist-diff-unit-name {
  font-size: 0.8rem;
  font-weight: 500;
  color: #e5e7eb;
}

.hist-diff-unit-serie {
  font-size: 0.72rem;
  color: #cbd5f5;
}

.hist-diff-unit-tags {
  display: flex;
  gap: 0.35rem;
  margin-top: 0.15rem;
}

.hist-tag {
  font-size: 0.68rem;
  padding: 0.06rem 0.48rem;
  border-radius: 999px;
  border: 1px solid transparent;
}

.hist-tag--nuevo {
  background: rgba(22, 163, 74, 0.25);
  border-color: rgba(34, 197, 94, 0.95);
  color: #bbf7d0;
}

.hist-tag--eliminado {
  background: rgba(248, 113, 113, 0.22);
  border-color: rgba(248, 113, 113, 0.95);
  color: #fee2e2;
}

.hist-tag--modificado {
  background: rgba(59, 130, 246, 0.25);
  border-color: rgba(96, 165, 250, 0.95);
  color: #dbeafe;
}

.hist-diff-unit-notes {
  margin-top: 0.25rem;
  border-radius: 0.7rem;
  background: rgba(15, 23, 42, 0.98);
  padding: 0.3rem 0.4rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  border: 1px solid rgba(51, 65, 85, 0.9);
}

.hist-diff-unit-row {
  display: grid;
  grid-template-columns: minmax(0, 110px) minmax(0, 1fr) auto minmax(0, 1fr);
  gap: 0.2rem 0.35rem;
  align-items: center;
  font-size: 0.72rem;
}

.hist-diff-unit-row .hist-diff-field {
  font-size: 0.7rem;
}

/* SCROLLBARS */
.hist-version-list::-webkit-scrollbar,
.hist-units-list::-webkit-scrollbar,
.hist-observaciones-text::-webkit-scrollbar {
  width: 6px;
}

.hist-version-list::-webkit-scrollbar-thumb,
.hist-units-list::-webkit-scrollbar-thumb,
.hist-observaciones-text::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.7);
}

.hist-version-list::-webkit-scrollbar-track,
.hist-units-list::-webkit-scrollbar-track,
.hist-observaciones-text::-webkit-scrollbar-track {
  background: transparent;
}

/* RESPONSIVE */
@media (max-width: 1024px) {
  .hist-layout {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto minmax(0, 1fr);
  }

  .hist-sidebar {
    order: 1;
  }

  .hist-content {
    order: 2;
  }
}

@media (max-width: 768px) {
  .hist-modal-card {
    max-width: 100%;
    max-height: 100vh;
    border-radius: 0;
  }

  .hist-toolbar {
    flex-direction: column;
    align-items: flex-start;
  }

  .hist-toolbar-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .hist-panels {
    grid-template-columns: minmax(0, 1fr);
  }

  .hist-kv-list {
    grid-template-columns: minmax(0, 1fr);
  }

  .hist-diff-item {
    grid-template-columns: minmax(0, 120px) minmax(0, 1fr);
    grid-template-rows: auto auto;
  }

  .hist-diff-arrow {
    display: none;
  }
}
</style>
